/**
 * Cloud shadows on everything else on the ground: models (GLBs) that receive shadows, the shadow
 * ground plane, and the water's sun glint.
 *
 * The scan gets its cloud shadows in the relighting snippet (cloudShaders.ts CLOUD_SHADOW_*). The
 * lit shaders of models need the same column-density lookup, applied to the clouds' sun only.
 * PlayCanvas 2.21 has no cookies for directional lights, so the receiving materials get a patched
 * `lightFunctionLightPS` chunk: right where a directional light's attenuation starts, the light
 * that is the clouds' sun (matched by direction) is multiplied by the cloud shadow at the
 * fragment, and a shadow catcher (the ground plane) accumulates it like a shadow. Both backends.
 *
 * The uniforms are the scan's, under their own names (`uCloudMeshShadow*`), set once in the
 * device's global scope rather than on every material. Nothing is patched, bound or compiled in a
 * scene without clouds casting shadows; when the last such layer goes, the materials are restored.
 */
import * as pc from 'playcanvas';
import { CLOUD_SHADOW_GLSL, CLOUD_SHADOW_UNIFORMS, CLOUD_SHADOW_WGSL } from './cloudShaders';

const rename = (code: string): string => code
    .replace(/uCloudShadow/g, 'uCloudMeshShadow')
    .replace(/splatCloudShadowFactor/g, 'meshCloudShadowFactor')
    .replace(/rl_cloudShadowSlot/g, 'mesh_cloudShadowSlot');

/** The mesh variant's uniform names (the scan's, renamed). */
export const MESH_CLOUD_SHADOW_UNIFORMS = {
    params: rename(CLOUD_SHADOW_UNIFORMS.params),
    light: rename(CLOUD_SHADOW_UNIFORMS.light),
    wind: rename(CLOUD_SHADOW_UNIFORMS.wind),
    box: rename(CLOUD_SHADOW_UNIFORMS.box),
    matrix: rename(CLOUD_SHADOW_UNIFORMS.matrix),
    global: rename(CLOUD_SHADOW_UNIFORMS.global),
    maps: CLOUD_SHADOW_UNIFORMS.maps.map(rename),
    /** xyz: the direction the clouds' sun light travels (−toward the sun). */
    sun: 'uCloudMeshSun',
} as const;

/** `meshCloudShadowFactor(worldPos)`: the cloud shadow at a world position (1 = no shadow). */
export const MESH_CLOUD_SHADOW_CORE_GLSL = /* glsl */ `
#ifndef CLOUD_MESH_SHADOW_CORE
#define CLOUD_MESH_SHADOW_CORE
${rename(CLOUD_SHADOW_GLSL)}
#endif
`;
export const MESH_CLOUD_SHADOW_CORE_WGSL = /* wgsl */ `
#ifndef CLOUD_MESH_SHADOW_CORE
#define CLOUD_MESH_SHADOW_CORE
${rename(CLOUD_SHADOW_WGSL)}
#endif
`;

const LIGHT_HELPER_GLSL = /* glsl */ `
#ifndef CLOUD_MESH_SHADOW_LIGHT
#define CLOUD_MESH_SHADOW_LIGHT
uniform vec4 ${MESH_CLOUD_SHADOW_UNIFORMS.sun};
// the cloud shadow for a directional light: only the clouds' sun is blocked by them
float meshCloudShadowFor(vec3 lightDir) {
    if (dot(lightDir, ${MESH_CLOUD_SHADOW_UNIFORMS.sun}.xyz) < 0.999) return 1.0;
    return meshCloudShadowFactor(vPositionW);
}
#endif
`;
const LIGHT_HELPER_WGSL = /* wgsl */ `
#ifndef CLOUD_MESH_SHADOW_LIGHT
#define CLOUD_MESH_SHADOW_LIGHT
uniform ${MESH_CLOUD_SHADOW_UNIFORMS.sun}: vec4f;
fn meshCloudShadowFor(lightDir: vec3f) -> f32 {
    if (dot(lightDir, uniform.${MESH_CLOUD_SHADOW_UNIFORMS.sun}.xyz) < 0.999) { return 1.0; }
    return meshCloudShadowFactor(vPositionW);
}
#endif
`;

// Where a directional light's attenuation starts (whitespace differs between engine builds:
// spaces in the debug build, tabs in the release build)
const ANCHOR_GLSL = /dLightDirNormW = light\{i\}_direction;[ \t]*\r?\n[ \t]*dAtten = 1\.0;/;
const ANCHOR_WGSL = /dLightDirNormW = uniform\.light\{i\}_direction;[ \t]*\r?\n[ \t]*dAtten = 1\.0;/;

/** The engine's lightFunctionLightPS with the cloud shadow on the clouds' sun; null if the chunk changed shape. */
export function patchLightFunctionGLSL(base: string): string | null {
    if (!ANCHOR_GLSL.test(base)) return null;
    return MESH_CLOUD_SHADOW_CORE_GLSL + LIGHT_HELPER_GLSL + base.replace(ANCHOR_GLSL, (anchor) => `${anchor}
        float cloudShadow{i} = meshCloudShadowFor(light{i}_direction);
        dAtten *= cloudShadow{i};
        #if defined(LIT_SHADOW_CATCHER)
            dShadowCatcher *= cloudShadow{i};
        #endif`);
}
export function patchLightFunctionWGSL(base: string): string | null {
    if (!ANCHOR_WGSL.test(base)) return null;
    return MESH_CLOUD_SHADOW_CORE_WGSL + LIGHT_HELPER_WGSL + base.replace(ANCHOR_WGSL, (anchor) => `${anchor}
        let cloudShadow{i} = meshCloudShadowFor(uniform.light{i}_direction);
        dAtten = dAtten * cloudShadow{i};
        #if defined(LIT_SHADOW_CATCHER)
            dShadowCatcher = dShadowCatcher * cloudShadow{i};
        #endif`);
}

const CHUNK = 'lightFunctionLightPS';

type ChunkedMaterial = pc.Material & { getShaderChunks?: (lang: string) => { get(n: string): string | undefined; set(n: string, c: string): unknown; delete(n: string): unknown } };

/** Patches and restores the receiving materials; binds the uniforms in the global scope. */
export class CloudMeshShadows {
    private readonly device: pc.GraphicsDevice;
    private readonly patched = new Set<ChunkedMaterial>();
    private code: { glsl: string; wgsl: string } | null | undefined;
    private nextWalk = 0;
    /** Materials patched so far (diagnostics). */
    get patchedCount(): number {
        return this.patched.size;
    }

    /** `engineChunks`: the engine's lightFunctionLightPS per language (tests; the device's by default). */
    constructor(device: pc.GraphicsDevice, private readonly engineChunks?: { glsl: string; wgsl: string }) {
        this.device = device;
    }

    private patchedCode(): { glsl: string; wgsl: string } | null {
        if (this.code === undefined) {
            const glsl = patchLightFunctionGLSL(this.engineChunks?.glsl ?? pc.ShaderChunks.get(this.device, pc.SHADERLANGUAGE_GLSL).get(CHUNK) ?? '');
            const wgsl = patchLightFunctionWGSL(this.engineChunks?.wgsl ?? pc.ShaderChunks.get(this.device, pc.SHADERLANGUAGE_WGSL).get(CHUNK) ?? '');
            this.code = glsl && wgsl ? { glsl, wgsl } : null;
            if (!this.code) console.warn('[StorySplat Viewer] cloud shadows on models: the engine light chunk changed, skipped');
        }
        return this.code;
    }

    /**
     * Keep the receivers patched while clouds cast shadows (`active`); restore them when not.
     * Receivers are looked up again about once a second (models load in late).
     */
    sync(active: boolean, receivers: () => pc.Entity[], now: number): void {
        if (!active) {
            if (this.patched.size) this.restoreAll();
            this.nextWalk = 0;
            return;
        }
        if (now < this.nextWalk) return;
        this.nextWalk = now + 1000;
        const code = this.patchedCode();
        if (!code) return;
        for (const root of receivers()) {
            if (!root || (root as unknown as { _destroyed?: boolean })._destroyed) continue;
            const renders = root.findComponents('render') as pc.RenderComponent[];
            for (const render of renders) {
                if (!render.receiveShadows) continue;
                for (const mi of render.meshInstances ?? []) this.patch(mi.material as ChunkedMaterial, code);
            }
            const models = root.findComponents('model') as pc.ModelComponent[];
            for (const model of models) {
                if (!model.receiveShadows) continue;
                for (const mi of model.meshInstances ?? []) this.patch(mi.material as ChunkedMaterial, code);
            }
        }
    }

    private patch(material: ChunkedMaterial | null, code: { glsl: string; wgsl: string }): void {
        if (!material || this.patched.has(material) || !material.getShaderChunks) return;
        // lit materials only (StandardMaterial / LitMaterial), and never over someone else's chunk
        if (!(material instanceof pc.StandardMaterial) && !(material instanceof (pc as unknown as { LitMaterial: typeof pc.Material }).LitMaterial)) return;
        const glsl = material.getShaderChunks(pc.SHADERLANGUAGE_GLSL);
        const wgsl = material.getShaderChunks(pc.SHADERLANGUAGE_WGSL);
        if (glsl.get(CHUNK) || wgsl.get(CHUNK)) return;
        glsl.set(CHUNK, code.glsl);
        wgsl.set(CHUNK, code.wgsl);
        material.update();
        this.patched.add(material);
    }

    private restoreAll(): void {
        for (const material of this.patched) {
            const glsl = material.getShaderChunks?.(pc.SHADERLANGUAGE_GLSL);
            const wgsl = material.getShaderChunks?.(pc.SHADERLANGUAGE_WGSL);
            if (glsl?.get(CHUNK) === this.code?.glsl) glsl?.delete(CHUNK);
            if (wgsl?.get(CHUNK) === this.code?.wgsl) wgsl?.delete(CHUNK);
            material.update();
        }
        this.patched.clear();
    }

    /** Global-scope uniforms read by every patched material and the water. */
    bind(values: { params: Float32Array; light: Float32Array; wind: Float32Array; box: Float32Array; matrices: Float32Array; global: Float32Array; maps: pc.Texture[]; sunTravel: [number, number, number] }): void {
        const scope = this.device.scope;
        const u = MESH_CLOUD_SHADOW_UNIFORMS;
        scope.resolve(u.params).setValue(values.params);
        scope.resolve(u.light).setValue(values.light);
        scope.resolve(u.wind).setValue(values.wind);
        scope.resolve(u.box).setValue(values.box);
        scope.resolve(u.matrix).setValue(values.matrices);
        // meshes don't use the scan's "relighting off" path: x = clouds cast, y = 1
        scope.resolve(u.global).setValue(new Float32Array([values.global[0], 1, 0, 0]));
        u.maps.forEach((name, i) => scope.resolve(name).setValue(values.maps[i]));
        scope.resolve(u.sun).setValue(new Float32Array([...values.sunTravel, 0]));
    }

    destroy(): void {
        if (this.patched.size) this.restoreAll();
    }
}
